import { OrniTransportError } from "./transport.ts";

/** Version of the Agent/CLI/API contract introduced in Phase 1. */
export const AGENT_PROTOCOL_SCHEMA_VERSION = 1;

/** Stable identifiers. The registry is authoritative for which values exist. */
export type AgentId = string & { readonly __orniAgentId: unique symbol };
export type CliVariantId = string & { readonly __orniCliVariantId: unique symbol };
export type ApiProfileId = string & { readonly __orniApiProfileId: unique symbol };
export type LegacyProviderId = "codex" | "claude" | "opencode";
/**
 * Provider identifiers introduced after the legacy SDK contract. The value
 * remains opaque until an Agent registry/Adapter explicitly supports it.
 * Keeping this brand prevents callers from accidentally treating an unknown
 * provider as one of the legacy execution paths.
 */
export type OpaqueProviderId = string & { readonly __orniOpaqueProviderId: unique symbol };
export type ProviderId = LegacyProviderId | OpaqueProviderId;

export type CapabilityState = "verified" | "degraded" | "unsupported" | "unknown";
export type OperationStatus =
  | "queued"
  | "validating"
  | "running"
  | "succeeded"
  | "failed"
  | "cancelled"
  | "expired"
  | "rejected"
  | "unsupported";
export type HistoryImportState = "unimported" | "pending" | "imported" | "failed";
export type HistoryScanState = "idle" | "scanning" | "complete" | "failed" | "cancelled";

const IDENTIFIER = /^[a-z][a-z0-9._-]{0,63}$/;

const LEGACY_PROVIDER_MAPPING: Record<string, { agentId: string; cliVariantId: string; providerId: LegacyProviderId }> = {
  codex: { agentId: "codex", cliVariantId: "codex", providerId: "codex" },
  "codex-cli": { agentId: "codex", cliVariantId: "codex", providerId: "codex" },
  claude: { agentId: "claude", cliVariantId: "claude-code", providerId: "claude" },
  "claude-code": { agentId: "claude", cliVariantId: "claude-code", providerId: "claude" },
  opencode: { agentId: "opencode", cliVariantId: "opencode", providerId: "opencode" },
};

// Keep compatibility callers aligned with the Agent identity registry. The
// mapping contains aliases (for example `claude-code`), so derive a unique
// canonical provider list instead of maintaining another provider array.
const CANONICAL_LEGACY_PROVIDER_IDS = Array.from(
  new Set(Object.values(LEGACY_PROVIDER_MAPPING).map((entry) => entry.providerId)),
);

/** Return the canonical legacy Provider ids accepted by the Agent registry. */
export function supportedLegacyProviderIds(): LegacyProviderId[] {
  return [...CANONICAL_LEGACY_PROVIDER_IDS];
}

export interface AgentIdentity {
  agentId: AgentId;
  cliVariantId: CliVariantId;
  apiProfileId: ApiProfileId | null;
  legacyProviderId: LegacyProviderId | null;
}

/**
 * Decode a provider identifier without collapsing future values into a
 * legacy provider. Known CLI aliases are retained for their canonical
 * providers; all other valid identifiers are returned as opaque values.
 */
export function decodeProviderId(value: unknown, path = "provider_id"): ProviderId {
  const normalized = requireIdentifier(text(value, path), path);
  const legacy = LEGACY_PROVIDER_MAPPING[normalized];
  return (legacy?.providerId ?? normalized) as ProviderId;
}

/** Return true only for providers with a legacy SDK execution contract. */
export function isLegacyProviderId(value: string): value is LegacyProviderId {
  return CANONICAL_LEGACY_PROVIDER_IDS.includes(value as LegacyProviderId);
}

/**
 * Enforce the legacy execution boundary used by current create-session
 * payloads. Unknown Agent IDs remain readable/projectable, but cannot be
 * silently assigned a terminal/native integration mode by this SDK.
 */
export function requireLegacyProviderId(value: unknown, path = "provider_id"): LegacyProviderId {
  const provider = decodeProviderId(value, path);
  if (!isLegacyProviderId(provider)) {
    throw new OrniTransportError(
      "UNSUPPORTED",
      `No verified SDK execution adapter is registered for provider ${provider}.`,
    );
  }
  return provider;
}

/**
 * Decode new identity fields while accepting the pre-Phase-1 provider_id.
 * A caller may not provide contradictory legacy and canonical identities.
 */
export function decodeAgentIdentity(value: unknown, path = "identity"): AgentIdentity {
  const row = record(value, path);
  const rawAgent = optionalText(row.agent_id ?? row.agentId, `${path}.agent_id`);
  const rawCli = optionalText(
    row.cli_variant_id ?? row.cliVariantId ?? row.cli_id ?? row.cliId,
    `${path}.cli_variant_id`,
  );
  const rawApi = optionalText(row.api_profile_id ?? row.apiProfileId, `${path}.api_profile_id`);
  const rawProvider = optionalText(row.provider_id ?? row.providerId, `${path}.provider_id`);
  const legacy = rawProvider === undefined ? undefined : LEGACY_PROVIDER_MAPPING[rawProvider];
  // Unknown provider IDs are valid projection data. If canonical identity
  // fields are absent, use the provider itself as a conservative opaque
  // identity; never fabricate Codex/Claude/OpenCode semantics.
  const agentId = requireIdentifier(rawAgent ?? legacy?.agentId ?? rawProvider, `${path}.agent_id`);
  const cliVariantId = requireIdentifier(rawCli ?? legacy?.cliVariantId ?? agentId, `${path}.cli_variant_id`);
  if (legacy && (agentId !== legacy.agentId || cliVariantId !== legacy.cliVariantId)) {
    throw contractError(`${path} contains contradictory canonical and legacy identity fields`);
  }
  return {
    agentId: agentId as AgentId,
    cliVariantId: cliVariantId as CliVariantId,
    apiProfileId: rawApi === undefined ? null : (requireIdentifier(rawApi, `${path}.api_profile_id`) as ApiProfileId),
    legacyProviderId: legacy?.providerId ?? null,
  };
}

export interface AgentCapabilityDto {
  capability: string;
  state: CapabilityState;
  reason?: string | null;
  checkedAt?: string | null;
}

export interface AgentManifestDto {
  schemaVersion: number;
  correlationId: string;
  agentId: AgentId;
  cliVariantId: CliVariantId;
  adapterId: string;
  adapterVersion: string;
  displayName: string;
  vendor: string;
  supportedPlatforms: string[];
  capabilities: AgentCapabilityDto[];
}

export interface AgentOperationReceiptDto {
  schemaVersion: number;
  correlationId: string;
  operationId: string;
  commandId: string;
  targetDeviceId: string;
  status: OperationStatus;
  phase: string;
  revision: number;
  createdAt: string;
  updatedAt: string;
  retryable: boolean;
  errorCode: string | null;
}

export interface ErrorEnvelopeDto {
  schemaVersion: number;
  correlationId: string;
  code: string;
  retryable: boolean;
  safeMessage: string;
  details?: Record<string, unknown>;
}

export interface AgentHistoryItemDto {
  sourceId: string;
  conversationId: string;
  agentId: AgentId;
  cliVariantId: CliVariantId;
  workspacePath: string;
  updatedAt: string | null;
  importState: HistoryImportState;
  isOrniManaged: boolean;
}

export interface AgentHistoryPageDto {
  schemaVersion: number;
  correlationId: string;
  items: AgentHistoryItemDto[];
  nextCursor: string | null;
  indexRevision: number;
  scanState: HistoryScanState;
  stale: boolean;
  hasMore: boolean;
}

export interface SyncCursorDto {
  stream: string;
  sequence: number;
  revision: number;
}

export function decodeCapabilityState(value: unknown, path = "capability.state"): CapabilityState {
  if (value === "verified" || value === "degraded" || value === "unsupported" || value === "unknown") return value;
  throw contractError(`${path} is invalid`);
}

export function decodeAgentManifest(value: unknown): AgentManifestDto {
  const row = record(value, "AgentManifest");
  const identity = decodeAgentIdentity(row, "AgentManifest");
  const capabilities = array(row.capabilities, "AgentManifest.capabilities").map((item, index) => {
    const capability = record(item, `AgentManifest.capabilities[${index}]`);
    return {
      capability: text(capability.capability, `AgentManifest.capabilities[${index}].capability`),
      state: decodeCapabilityState(capability.state, `AgentManifest.capabilities[${index}].state`),
      reason: nullableText(capability.reason, `AgentManifest.capabilities[${index}].reason`),
      checkedAt: nullableText(capability.checked_at ?? capability.checkedAt, `AgentManifest.capabilities[${index}].checked_at`),
    };
  });
  return {
    schemaVersion: nonNegativeInteger(row.schema_version ?? row.schemaVersion, "AgentManifest.schema_version"),
    correlationId: text(row.correlation_id ?? row.correlationId, "AgentManifest.correlation_id"),
    agentId: identity.agentId,
    cliVariantId: identity.cliVariantId,
    adapterId: text(row.adapter_id ?? row.adapterId, "AgentManifest.adapter_id"),
    adapterVersion: text(row.adapter_version ?? row.adapterVersion, "AgentManifest.adapter_version"),
    displayName: text(row.display_name ?? row.displayName, "AgentManifest.display_name"),
    vendor: text(row.vendor, "AgentManifest.vendor"),
    supportedPlatforms: array(row.supported_platforms ?? row.supportedPlatforms, "AgentManifest.supported_platforms").map((item, index) => text(item, `AgentManifest.supported_platforms[${index}]`)),
    capabilities,
  };
}

export function decodeAgentOperationReceipt(value: unknown): AgentOperationReceiptDto {
  const row = record(value, "AgentOperationReceipt");
  const status = text(row.status, "AgentOperationReceipt.status");
  const allowed: OperationStatus[] = ["queued", "validating", "running", "succeeded", "failed", "cancelled", "expired", "rejected", "unsupported"];
  if (!allowed.includes(status as OperationStatus)) throw contractError("AgentOperationReceipt.status is invalid");
  return {
    schemaVersion: nonNegativeInteger(row.schema_version ?? row.schemaVersion, "AgentOperationReceipt.schema_version"),
    correlationId: text(row.correlation_id ?? row.correlationId, "AgentOperationReceipt.correlation_id"),
    operationId: text(row.operation_id ?? row.operationId, "AgentOperationReceipt.operation_id"),
    commandId: text(row.command_id ?? row.commandId, "AgentOperationReceipt.command_id"),
    targetDeviceId: text(row.target_device_id ?? row.targetDeviceId, "AgentOperationReceipt.target_device_id"),
    status: status as OperationStatus,
    phase: text(row.phase, "AgentOperationReceipt.phase"),
    revision: nonNegativeInteger(row.revision, "AgentOperationReceipt.revision"),
    createdAt: text(row.created_at ?? row.createdAt, "AgentOperationReceipt.created_at"),
    updatedAt: text(row.updated_at ?? row.updatedAt, "AgentOperationReceipt.updated_at"),
    retryable: boolean(row.retryable, "AgentOperationReceipt.retryable"),
    errorCode: nullableText(row.error_code ?? row.errorCode, "AgentOperationReceipt.error_code"),
  };
}

export function decodeErrorEnvelope(value: unknown): ErrorEnvelopeDto {
  const row = record(value, "ErrorEnvelope");
  const details = row.details;
  if (details !== undefined && (typeof details !== "object" || details === null || Array.isArray(details))) {
    throw contractError("ErrorEnvelope.details must be an object");
  }
  return {
    schemaVersion: nonNegativeInteger(row.schema_version ?? row.schemaVersion, "ErrorEnvelope.schema_version"),
    correlationId: text(row.correlation_id ?? row.correlationId, "ErrorEnvelope.correlation_id"),
    code: text(row.code, "ErrorEnvelope.code"),
    retryable: boolean(row.retryable, "ErrorEnvelope.retryable"),
    safeMessage: text(row.safe_message ?? row.safeMessage, "ErrorEnvelope.safe_message"),
    ...(details === undefined ? {} : { details: details as Record<string, unknown> }),
  };
}

export function decodeAgentHistoryPage(value: unknown): AgentHistoryPageDto {
  const row = record(value, "AgentHistoryPage");
  const items = array(row.items, "AgentHistoryPage.items").map((item, index) => {
    const entry = record(item, `AgentHistoryPage.items[${index}]`);
    const identity = decodeAgentIdentity(entry, `AgentHistoryPage.items[${index}]`);
    const importState = text(entry.import_state ?? entry.importState, `AgentHistoryPage.items[${index}].import_state`);
    if (!["unimported", "pending", "imported", "failed"].includes(importState)) throw contractError(`AgentHistoryPage.items[${index}].import_state is invalid`);
    return {
      sourceId: text(entry.source_id ?? entry.sourceId, `AgentHistoryPage.items[${index}].source_id`),
      conversationId: text(entry.conversation_id ?? entry.conversationId, `AgentHistoryPage.items[${index}].conversation_id`),
      agentId: identity.agentId,
      cliVariantId: identity.cliVariantId,
      workspacePath: text(entry.workspace_path ?? entry.workspacePath, `AgentHistoryPage.items[${index}].workspace_path`),
      updatedAt: nullableText(entry.updated_at ?? entry.updatedAt, `AgentHistoryPage.items[${index}].updated_at`),
      importState: importState as HistoryImportState,
      isOrniManaged: boolean(entry.is_orni_managed ?? entry.isOrniManaged, `AgentHistoryPage.items[${index}].is_orni_managed`),
    };
  });
  const scanState = text(row.scan_state ?? row.scanState, "AgentHistoryPage.scan_state");
  if (!["idle", "scanning", "complete", "failed", "cancelled"].includes(scanState)) throw contractError("AgentHistoryPage.scan_state is invalid");
  return {
    schemaVersion: nonNegativeInteger(row.schema_version ?? row.schemaVersion, "AgentHistoryPage.schema_version"),
    correlationId: text(row.correlation_id ?? row.correlationId, "AgentHistoryPage.correlation_id"),
    items,
    nextCursor: nullableText(row.next_cursor ?? row.nextCursor, "AgentHistoryPage.next_cursor"),
    indexRevision: nonNegativeInteger(row.index_revision ?? row.indexRevision, "AgentHistoryPage.index_revision"),
    scanState: scanState as HistoryScanState,
    stale: boolean(row.stale, "AgentHistoryPage.stale"),
    hasMore: boolean(row.has_more ?? row.hasMore, "AgentHistoryPage.has_more"),
  };
}

export function decodeSyncCursor(value: unknown): SyncCursorDto {
  const row = record(value, "SyncCursor");
  return {
    stream: text(row.stream, "SyncCursor.stream"),
    sequence: nonNegativeInteger(row.sequence, "SyncCursor.sequence"),
    revision: nonNegativeInteger(row.revision, "SyncCursor.revision"),
  };
}

function record(value: unknown, path: string): Record<string, unknown> {
  if (!value || typeof value !== "object" || Array.isArray(value)) throw contractError(`${path} must be an object`);
  return value as Record<string, unknown>;
}
function array(value: unknown, path: string): unknown[] {
  if (!Array.isArray(value)) throw contractError(`${path} must be an array`);
  return value;
}
function text(value: unknown, path: string): string {
  if (typeof value !== "string" || value.length === 0) throw contractError(`${path} must be non-empty text`);
  return value;
}
function optionalText(value: unknown, path: string): string | undefined {
  return value === undefined || value === null ? undefined : text(value, path);
}
function nullableText(value: unknown, path: string): string | null {
  return value === undefined || value === null ? null : text(value, path);
}
function requireIdentifier(value: string | undefined, path: string): string {
  if (!value || !IDENTIFIER.test(value)) throw contractError(`${path} must be a lowercase stable identifier`);
  return value;
}
function nonNegativeInteger(value: unknown, path: string): number {
  if (!Number.isSafeInteger(value) || (value as number) < 0) throw contractError(`${path} must be a non-negative integer`);
  return value as number;
}
function boolean(value: unknown, path: string): boolean {
  if (typeof value !== "boolean") throw contractError(`${path} must be boolean`);
  return value;
}
function contractError(message: string): OrniTransportError {
  return new OrniTransportError("CONTRACT_INVALID", `Agent protocol contract is invalid: ${message}`);
}
